import { cloneDeep } from "lodash";
import { isRegexMatch } from "@sscs/utils";
import type { AppDispatch } from "Store/store";
import {
  GetVersionsRequest,
  GetVersionsResponse,
  GroupUpdatesMessage,
  PackageProgressMessage,
  SearchProjectsRequest,
  SearchProjectsResponse,
} from "Types";
import {
  convertToProjectSummary,
  createProjectsNotification,
  packageCreated,
  packageDeleted,
  projectCreated,
  projectDeleted,
  sortProjects,
  sortVersions,
  versionCreated,
  versionDeleted,
} from "Utils";
import { getCurrentUserEmail, getWebsocketProtocolHost } from ".";

/***********************************/
/** Handle search endpoint updates */
/***********************************/
export const updateProjectSearchCache = (
  state: unknown,
  dispatch: AppDispatch,
  draft: SearchProjectsResponse,
  message: GroupUpdatesMessage,
  arg: SearchProjectsRequest,
  notifications: Array<string | number>,
): SearchProjectsResponse => {
  const currentUser = getCurrentUserEmail(state);

  if (message.entity === "project") {
    if (message.action === "created") {
      const matchesFilter = message.data.some((project) =>
        isRegexMatch(project.name, arg.searchName ?? ""),
      );

      if (!matchesFilter) return draft;

      if (currentUser !== message.initiated_by) {
        notifications.push(createProjectsNotification(message, message.data));
      }

      dispatch(
        projectCreated(
          message.data.map((project) => ({
            id: project.project_id,
            initiated_by: message.initiated_by,
          })),
        ),
      );

      const newProjects = message.data.filter(
        (project) =>
          !draft.projects.some(
            (existing) => existing.project_id === project.project_id,
          ),
      );

      if (newProjects.length === 0) return draft;

      return {
        match_count: draft.match_count + newProjects.length,
        projects: sortProjects(
          draft.projects.concat(
            newProjects.map((project) => convertToProjectSummary(project)),
          ),
        ),
      };
    } else if (message.action === "deleted") {
      const deletedProjects = draft.projects.filter((project) =>
        message.keys.includes(project.project_id),
      );

      // Deleted project is not in cache which means user doesn't see it
      // Happens only when filter is used
      if (deletedProjects.length === 0) return draft;

      if (currentUser !== message.initiated_by) {
        notifications.push(
          createProjectsNotification(message, deletedProjects),
        );
      }

      dispatch(
        projectDeleted(
          message.keys.map((projectId) => ({
            id: projectId,
            initiated_by: message.initiated_by,
          })),
        ),
      );

      return {
        match_count: draft.match_count - message.keys.length,
        projects: draft.projects.filter(
          (project) =>
            !message.keys.some(
              (deletedProjectId) => deletedProjectId === project.project_id,
            ),
        ),
      };
    } else if (message.action === "updated") {
      const nonUpdatedProjects = draft.projects.filter(
        (project) => !message.keys.includes(project.project_id),
      );

      if (nonUpdatedProjects.length === 0 && message.data.length === 0)
        return draft;

      const matchedProjects = nonUpdatedProjects.concat(
        message.data
          .filter(
            (project) =>
              isRegexMatch(project.name, arg.searchName ?? "") ||
              project.packages.some((pkg) =>
                isRegexMatch(pkg.name, arg.searchName ?? ""),
              ),
          )
          .map((project) => {
            if (!isRegexMatch(project.name, arg.searchName ?? "")) {
              project.packages = project.packages.filter((pkg) =>
                isRegexMatch(pkg.name, arg.searchName ?? ""),
              );
            }
            return convertToProjectSummary(project);
          }),
      );

      return {
        match_count: matchedProjects.length,
        projects: sortProjects(matchedProjects),
      };
    }
  } else if (message.entity === "package") {
    if (message.action === "created")
      dispatch(
        packageCreated(
          message.keys.map((packageId) => ({
            id: packageId,
            initiated_by: message.initiated_by,
          })),
        ),
      );
    else if (message.action === "deleted")
      dispatch(
        packageDeleted(
          message.keys.map((packageId) => ({
            id: packageId,
            initiated_by: message.initiated_by,
          })),
        ),
      );
  }

  return draft;
};

/******************************************/
/** Handle get versions endpoint updates */
/******************************************/
export const updateGetVersionsCache = (
  state: unknown,
  dispatch: AppDispatch,
  draft: GetVersionsResponse,
  message: GroupUpdatesMessage,
  arg: GetVersionsRequest,
): GetVersionsResponse => {
  const currentUser = getCurrentUserEmail(state);

  if (message.entity !== "version") return draft;

  if (message.action === "created") {
    // We are interested only in versions for the current project
    const createdVersions = message.data.filter(
      (version) => version.package_id === arg.packageId,
    );

    // created versions aren't for the current project
    if (createdVersions.length === 0) return draft;

    if (message.initiated_by !== currentUser) {
      dispatch(
        versionCreated(
          createdVersions.map((version) => ({
            id: version.version_id,
            initiated_by: message.initiated_by,
          })),
        ),
      );
    } else {
      const newVersions = createdVersions.filter(
        (created) =>
          !draft.versions.some(
            (version) => version.version_id === created.version_id,
          ),
      );

      if (newVersions.length === 0) return draft;

      return {
        match_count: draft.match_count + newVersions.length,
        max_versions_per_package: draft.max_versions_per_package,
        versions: sortVersions(newVersions.concat(draft.versions)),
      };
    }
  } else if (message.action === "deleted") {
    if (message.initiated_by !== currentUser) {
      dispatch(
        versionDeleted(
          message.keys.map((versionId) => ({
            id: versionId,
            initiated_by: message.initiated_by,
          })),
        ),
      );
    } else {
      return {
        match_count: draft.match_count - message.keys.length,
        max_versions_per_package: draft.max_versions_per_package,
        versions: draft.versions.filter(
          (version) =>
            !message.keys.some(
              (deletedVersionId) => deletedVersionId === version.version_id,
            ),
        ),
      };
    }
  } else if (message.action === "updated") {
    const modifiedVersions = message.data.filter(
      (version) => version.package_id === arg.packageId,
    );

    if (modifiedVersions.length === 0) return draft;

    const mergedVersions = draft.versions.map((version) => {
      const updated = modifiedVersions.find(
        (item) => version.version_id === item.version_id,
      );

      return updated ?? version;
    });

    return {
      match_count: mergedVersions.length,
      max_versions_per_package: draft.max_versions_per_package,
      versions: mergedVersions,
    };
  }

  return draft;
};

export const listenToProgressUpdates = async (
  cacheDataLoaded: Promise<unknown>,
  cacheEntryRemoved: Promise<unknown>,
  packageId: string,
  listener: (event: MessageEvent) => void,
) => {
  const progressUpdates = new WebSocket(
    `${getWebsocketProtocolHost()}/websocket/packages/${packageId}/progress/`,
  );

  try {
    await cacheDataLoaded;

    progressUpdates.addEventListener("message", listener);
  } catch {
    // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
    // in which case `cacheDataLoaded` will throw
  }

  await cacheEntryRemoved;
  progressUpdates.removeEventListener("message", listener);
  progressUpdates.close();
};

export const updateProgress = (
  draft: GetVersionsResponse,
  message: PackageProgressMessage,
): GetVersionsResponse => {
  if (message.action !== "updated") {
    return draft;
  }

  return {
    match_count: draft.match_count,
    max_versions_per_package: draft.max_versions_per_package,
    versions: draft.versions.map((version) => {
      const updatedStatus = message.data.find(
        (item) => item.version_id === version.version_id,
      )?.current_status;

      if (updatedStatus) {
        const updatedVersion = cloneDeep(version);
        updatedVersion.artifacts_summary.version.current_status = updatedStatus;

        return updatedVersion;
      } else {
        return version;
      }
    }),
  };
};
